import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { ChevronRight, Target } from 'lucide-react';
import useCameraViewport from '../utils/useCameraViewport';

/**
 * Welcome screen shown once to every new user before the NDA/Consent screen.
 * Persona: Yuri — infantry veteran + 26-year pro chef + fitness coach.
 * Tone: tactical, no-excuses, warm mentor.
 *
 * Persistence: localStorage flag `yumix_welcome_seen_<userId>`.
 * After acknowledgement, the parent unmounts this and shows ConsentScreen.
 *
 * Iter. 406.fr.55 — Fully i18n-driven (fr/en/es).
 */
/* eslint-disable react/no-unescaped-entities */
export default function WelcomeScreen({ userId, userName, onAcknowledge }) {
  const { t } = useTranslation();
  const [pressed, setPressed] = useState(false);
  const firstName = (userName || '').trim().split(' ')[0] || '';

  // Iter. 406.g — iOS-safe fullscreen shell with internal scroll for long copy
  const { containerStyle } = useCameraViewport({ open: true, allowInternalScroll: true });

  const handleAcknowledge = () => {
    if (pressed) return;
    setPressed(true);
    try {
      if (userId) {
        localStorage.setItem(`yumix_welcome_seen_${userId}`, new Date().toISOString());
      }
    } catch {
      /* ignore storage errors */
    }
    onAcknowledge?.();
  };

  // "YUMIX, George !" vs "YUMIX !" — inject comma + first name via placeholder
  const nameSuffix = firstName ? `, ${firstName}` : '';
  const titleLine2 = t('welcome.title_line2', { comma: nameSuffix }).replace('{{comma}}', nameSuffix);

  return (
    <div
      className="fixed inset-0 z-[100] bg-background"
      style={containerStyle}
      data-testid="welcome-screen"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[420px] h-[420px] rounded-full bg-amber-500/10 blur-3xl" />
      </div>

      <div className="relative z-10 min-h-full flex flex-col">
        {/* Header */}
        <div className="px-5 pt-10 pb-4 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center">
              <Target className="w-5 h-5 text-primary" />
            </div>
            <p className="font-heading uppercase tracking-[0.2em] text-primary text-sm">
              {t('welcome.brand')}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-5 sm:px-8 pb-8">
          <div className="max-w-2xl mx-auto">
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight text-foreground leading-[1.05] mt-4">
              {t('welcome.title_line1')}
              <br />
              <span className="text-primary">{titleLine2}</span>
            </h1>

            <div className="mt-7 h-px w-16 bg-primary/60" />

            <div className="mt-7 space-y-5 text-[15px] sm:text-base text-foreground/90 leading-relaxed font-light">
              <p>{t('welcome.p1')}</p>

              <p>
                {t('welcome.p2_before_years1')}
                <span className="text-foreground font-medium">{t('welcome.p2_years1')}</span>
                {t('welcome.p2_middle')}
                <span className="text-foreground font-medium">{t('welcome.p2_years2')}</span>
                {t('welcome.p2_after_years2')}
                <span className="font-heading text-primary">{t('welcome.p2_extra_miles')}</span>
                {t('welcome.p2_and')}
                <span className="italic">{t('welcome.p2_science')}</span>
                {t('welcome.p2_end')}
              </p>

              <p>{t('welcome.p3')}</p>

              <p className="text-foreground font-medium">{t('welcome.p4_bold')}</p>

              <p>
                {t('welcome.p5_before_bold')}
                <span className="text-foreground font-medium">{t('welcome.p5_bold')}</span>
                {t('welcome.p5_after_bold')}
              </p>

              <p className="font-heading uppercase tracking-wide text-primary text-lg pt-2">
                {t('welcome.cta_hero')}
              </p>
            </div>

            {/* Signature */}
            <div className="mt-8 pt-5 border-t border-border/30">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {t('welcome.signature_name')}
              </p>
              <p className="text-[11px] text-muted-foreground/70 mt-1">
                {t('welcome.signature_role')}
              </p>
            </div>

            {/* CTA */}
            <div className="mt-8 pb-10">
              <Button
                onClick={handleAcknowledge}
                disabled={pressed}
                size="lg"
                className="w-full h-14 font-heading uppercase tracking-wide text-base bg-primary text-primary-foreground hover:bg-primary/90 transition-all group"
                data-testid="welcome-acknowledge-btn"
              >
                {pressed ? t('welcome.btn_loading') : t('welcome.btn_default')}
                <ChevronRight className="w-5 h-5 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
              </Button>
              <p className="text-[11px] text-muted-foreground/70 text-center mt-3">
                {t('welcome.footnote')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
